'use client';

import type { CSSProperties, ReactNode } from 'react';

import type { popstore } from '@ps/types';

import { CustomButtonContainer } from '../../common/components/CustomButtonContainer';
import { useMagicVideoBg } from '../hooks/useMagicVideoBg';

interface Props {
  /** The button's magic-clip video config. */
  videoBg: popstore.ButtonBgAttrs | undefined;
  preview?: boolean;
  /** Still image/icon URL the video swaps to after playing twice. */
  fallbackUrl?: string | null;
  /** Computed button style for the container (same as the pure path). */
  buttonStyle: CSSProperties;
  /** Variant-specific extra container style (e.g. `cursor: pointer`). */
  extraStyle?: CSSProperties;
  className?: string;
  /**
   * The full static button, with icons/thumbnail shown. Painted when there is no
   * clip at all, or once the clip has finished its two plays — NOT merely while
   * the clip is off-screen/not yet started, since upstream keeps the still
   * content suppressed for the whole life of a configured clip.
   *
   * Both slots are plain rendered nodes (not a render-prop function) precisely
   * so a Server Component can build them and pass them to this client island:
   * React elements serialize across the server→client boundary, functions do
   * not. This is what lets each variant's `*View` stay a Server Component and
   * render `MagicVideoBg` directly — no per-variant client wrapper.
   */
  staticContent: ReactNode;
  /**
   * Content painted while the clip IS playing — the same button with the
   * icons/thumbnail hidden so they don't cover the video. Variants build this
   * by rendering their content component with its `show*` flag set to false.
   */
  playingContent: ReactNode;
}

/**
 * Client island that owns the magic-clip video background: the
 * IntersectionObserver, the play-twice-then-swap state, the container ref, and
 * the container's while-playing aspect-ratio override. A variant passes its
 * PURE view as the `staticContent` / `playingContent` nodes and composes this
 * only when a clip is configured — so a button WITHOUT a magic clip stays fully
 * server-rendered.
 */
export function MagicVideoBg({
  videoBg,
  preview,
  fallbackUrl,
  buttonStyle,
  extraStyle,
  className,
  staticContent,
  playingContent,
}: Props) {
  const {
    containerRef,
    videoUrl,
    showVideo,
    showStaticContent,
    aspectRatio,
    videoProps,
  } = useMagicVideoBg({ videoBg, preview, fallbackUrl });

  // Only an aspect-ratio-locked clip drives the height: apply its ratio and
  // drop the wrapper's minHeight so the ratio governs. When the clip is NOT
  // locked (`aspectRatio == null`) the button keeps its normal per-type
  // minHeight (80 / 100 for text) and the video simply `object-cover`s it —
  // 1:1 with legacy RN `text.tsx`, where the container's `minHeight: undefined`
  // is a StyleSheet no-op so `buttonStyle.minHeight` survives. On web an
  // explicit `minHeight: undefined` CLEARS the wrapper default, collapsing the
  // button to the (absolutely-positioned, zero-height) video — the "small /
  // cut off" bug. So only nuke minHeight when a ratio actually replaces it.
  const videoInnerStyle: CSSProperties | undefined =
    !videoUrl || aspectRatio == null
      ? undefined
      : { aspectRatio, minHeight: undefined };
  const innerStyle =
    videoInnerStyle || extraStyle
      ? { ...videoInnerStyle, ...extraStyle }
      : undefined;

  return (
    <CustomButtonContainer
      containerRef={containerRef}
      buttonStyle={buttonStyle}
      style={innerStyle}
      className={className}
    >
      {videoUrl && showVideo ? (
        <video
          {...videoProps}
          className="absolute inset-0 h-full w-full object-cover"
          src={videoUrl}
        />
      ) : null}
      {showStaticContent ? staticContent : playingContent}
    </CustomButtonContainer>
  );
}
